import { Staff } from "gen/company/models_pb"
import { UUID as PBUUID } from "gen/proto/uuid/models_pb"
import { BoolFilter } from "gen/search/service/service_pb"
import { useMemo } from "react"
import { UUID } from "shared-ts/classes/uuid"

import { useSearchStaffs } from "features/api/search"

import { UsePopoverMultiSelectProps, UsePopoverMultiSelectRes } from "./types"
import usePopoverMultiSelect from "./usePopoverMultiSelect"

type UseStaffsPopoverMultiSelectProps = Omit<
  UsePopoverMultiSelectProps<Staff>,
  | "items"
  | "keyExtractor"
  | "labelExtractor"
  | "contentExtractor"
  | "onChangeQuery"
> & {
  roleId?: string
}

export interface UseStaffsPopoverMultiSelectRes
  extends UsePopoverMultiSelectRes<Staff> {
  selectedIds: PBUUID[]
}

function useStaffsPopoverMultiSelect(
  props: UseStaffsPopoverMultiSelectProps = {}
): UseStaffsPopoverMultiSelectRes {
  const { roleId, ...rest } = props
  const { results, setQuery } = useSearchStaffs(
    { isActiveFilter: BoolFilter.TRUE },
    { ignoreSearchQuery: true }
  )

  const staffs = useMemo(() => {
    const out: Staff[] = []
    for (const r of results) {
      if (r.staff && (!roleId || r.staff.roleIds.includes(roleId))) {
        out.push(r.staff)
      }
    }
    return out
  }, [results, roleId])

  const res = usePopoverMultiSelect({
    items: staffs,
    keyExtractor,
    labelExtractor,
    onChangeQuery: setQuery,
    ...rest,
  })

  const selectedIds = useMemo(() => {
    const out: PBUUID[] = []
    for (const staff of res.selected ?? []) {
      if (staff.id) out.push(staff.id)
    }
    return out
  }, [res.selected])

  return {
    ...res,
    selectedIds,
  }
}

function keyExtractor(staff: Staff): string {
  return UUID.idString(staff.id)
}

function labelExtractor(staff: Staff): string {
  return `${staff.firstName} ${staff.lastName}`
}

export default useStaffsPopoverMultiSelect
